// side-effect import: polyfill.ts monkeypatches Math/Array/String prototypes and
// has no exports, so it must be a bare import or esbuild tree-shakes it away.
import "./polyfill.js";
import * as typesystem from "./typesystem.js";
// re-export the class system (the original copied typesystem.* onto util.exports)
export * from "./typesystem.js";
// local alias for internal use (the copy loop used to make `Class` available here)
const Class = typesystem.Class;

window.tm = 0.0;

const time_ms = function time_ms() {
  if (window.performance) return window.performance.now();
  else return new Date().getMilliseconds();
};

//unordered array
class RandomArray<T> extends Array<T> {
  //pop by swapping with last item in unordered array
  pop_i(i: number): T {
    if (this.length < 2) {
      const ret = this[i];

      this.length = 0;
      return ret;
    }

    const ret = this[i];
    this[i] = this[this.length - 1];
    this.length--;

    return ret;
  }

  remove(item: T, no_error?: boolean): void {
    const i = this.indexOf(item);

    if (i < 0) {
      if (!no_error) {
        console.trace("WARNING: item not in array!", item);
        return;
      } else {
        throw new Error("Item not in array: " + item);
      }
    }

    // NOTE: the original JS passes `item` (not the index `i`) here; preserved
    // verbatim to keep deterministic output byte-identical. See pop_i above.
    this.pop_i(item as unknown as number);
  }
}
typesystem.registerClass(RandomArray);

class cachering<T> extends Array<T> {
  cur: number;

  constructor(func: () => T, size: number) {
    super();

    this.cur = 0;

    for (let i = 0; i < size; i++) {
      this.push(func());
    }
  }

  static fromConstructor<T>(cls: new () => T, size: number): cachering<T> {
    const func = function () {
      return new cls();
    };

    return new cachering<T>(func, size);
  }

  next() {
    const ret = this[this.cur];
    this.cur = (this.cur + 1) % this.length;

    return ret;
  }
}

class SetIter {
  set: set;
  i: number;
  ret: { done: boolean; value: object | undefined };

  constructor(set: set) {
    this.set = set;
    this.i = 0;
    this.ret = { done: false, value: undefined };
  }

  [Symbol.iterator]() {
    return this;
  }

  next() {
    const ret = this.ret;

    while (this.i < this.set.items.length && this.set.items[this.i] === EmptySlot) {
      this.i++;
    }

    if (this.i >= this.set.items.length) {
      ret.done = true;
      ret.value = undefined;

      return ret;
    }

    ret.value = this.set.items[this.i++];
    return ret;
  }
}

var EmptySlot = {};

// The Symbol.keystr identity hook returns a string (primitives) or a numeric
// prototype id (Class()/registerClass); object keys coerce to string anyway, so
// normalize to string for the keys maps below.
type Keyable = object | string | number;
function keystrOf(item: Keyable): string {
  return "" + (item as Record<symbol, () => unknown>)[Symbol.keystr]();
}

class set {
  items: object[];
  keys: Record<string, number>;
  freelist: number[];
  length: number;

  constructor(input?: Iterable<object> | object[] | string) {
    this.items = [];
    this.keys = {};
    this.freelist = [];

    this.length = 0;

    if (input == undefined) {
      return;
    }

    if (typeof input == "string") {
      for (const ch of input) {
        this.add(ch as unknown as object);
      }
    } else {
      // arrays and any other iterable
      for (const item of input as Iterable<object>) {
        this.add(item);
      }
    }
  }

  [Symbol.iterator]() {
    return new SetIter(this);
  }

  add(item: object) {
    const key = keystrOf(item);

    if (key in this.keys) return;

    if (this.freelist.length > 0) {
      const i = this.freelist.pop()!;

      this.keys[key] = i;
      this.items[i] = item;
    } else {
      const i = this.items.length;

      this.keys[key] = i;
      this.items.push(item);
    }

    this.length++;
  }

  remove(item: object) {
    const key = keystrOf(item);

    if (!(key in this.keys)) {
      console.trace("Warning, item", item, "is not in set");
      return;
    }

    const i = this.keys[key];
    this.freelist.push(i);
    this.items[i] = EmptySlot;

    delete this.keys[key];

    this.length--;
  }

  has(item: object) {
    return keystrOf(item) in this.keys;
  }

  forEach(func: (item: object) => void, thisvar?: object) {
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];

      if (item === EmptySlot) continue;

      thisvar != undefined ? func.call(thisvar, item) : func(item);
    }
  }
}

const _hash_null = {};

class hashtable<V = unknown> {
  items: V[];
  _keys: Record<string, number>;
  length: number;

  constructor() {
    this.items = [];
    this._keys = {};
    this.length = 0;
  }

  set(key: Keyable, val: V) {
    const ks = keystrOf(key);

    let i;
    if (!(ks in this._keys)) {
      i = this.items.length;
      this.items.push(0 as unknown as V);
      this._keys[ks] = i;

      this.length++;
    } else {
      i = this._keys[ks];
    }

    this.items[i] = val;
  }

  remove(key: Keyable) {
    const ks = keystrOf(key);

    if (!(ks in this._keys)) {
      console.trace("Warning, key not in hashtable:", ks);
      return;
    }

    const i = this._keys[ks];
    this.items[i] = _hash_null as unknown as V;
    delete this._keys[ks];
    this.length--;
  }

  has(key: Keyable) {
    return keystrOf(key) in this._keys;
  }

  get(key: Keyable): V | undefined {
    const ks = keystrOf(key);
    if (!(ks in this._keys)) {
      console.trace("Warning, item not in hash", ks);
      return undefined;
    }

    return this.items[this._keys[ks]];
  }

  add(key: Keyable, val: V) {
    return this.set(key, val);
  }

  keys() {
    return Object.keys(this._keys);
  }

  values() {
    const ret = [];
    const len = this.items.length;

    for (let i = 0; i < len; i++) {
      const item = this.items[i];
      if (item !== _hash_null) ret.push(item);
    }

    return ret;
  }

  forEach(cb: (key: string, val: V) => void, thisvar?: object) {
    if (thisvar == undefined) thisvar = self;

    for (const k in this._keys) {
      const i = this._keys[k];
      cb.call(thisvar, k, this.items[i]);
    }
  }
}

class IDGen {
  _cur: number;

  constructor() {
    this._cur = 1;
  }

  next() {
    return this._cur++;
  }

  max_cur(id: number) {
    this._cur = Math.max(this._cur, id + 1);
  }

  toJSON() {
    return {
      _cur: this._cur,
    };
  }

  static fromJSON(obj: { _cur: number }) {
    const ret = new IDGen();
    ret._cur = obj._cur;
    return ret;
  }
}

function get_callstack(err?: Error): string[] {
  const callstack: string[] = [];
  let isCallstackPopulated = false;
  const err_was_undefined = err == undefined;

  if (err == undefined) {
    try {
      _idontexist.idontexist += 0; //doesn't exist- that's the point
    } catch (err1) {
      err = err1;
    }
  }

  if (err != undefined) {
    if (err.stack) {
      //Firefox
      const lines = err.stack.split("\n");
      const len = lines.length;
      for (let i = 0; i < len; i++) {
        if (1) {
          lines[i] = lines[i].replace(/@http\:\/\/.*\//, "|");
          const l = lines[i].split("|");
          lines[i] = l[1] + ": " + l[0];
          lines[i] = lines[i].trim();
          callstack.push(lines[i]);
        }
      }

      //Remove call to printStackTrace()
      if (err_was_undefined) {
        //callstack.shift();
      }
      isCallstackPopulated = true;
    } else if (window.opera && err?.message) {
      //Opera
      const lines = err.message.split("\n");
      const len = lines.length;
      for (let i = 0; i < len; i++) {
        if (lines[i].match(/^\s*[A-Za-z0-9\-_\$]+\(/)) {
          let entry = lines[i];
          //Append next line also since it has the file info
          if (lines[i + 1]) {
            entry += " at " + lines[i + 1];
            i++;
          }
          callstack.push(entry);
        }
      }
      //Remove call to printStackTrace()
      if (err_was_undefined) {
        callstack.shift();
      }
      isCallstackPopulated = true;
    }
  }

  const limit = 24;
  if (!isCallstackPopulated) {
    //IE and Safari
    let currentFunction = arguments.callee.caller;
    let i = 0;
    while (currentFunction && i < 24) {
      const fn = currentFunction.toString();
      const fname = fn.substring(fn.indexOf("function") + 8, fn.indexOf("")) || "anonymous";
      callstack.push(fname);
      currentFunction = currentFunction.caller;

      i++;
    }
  }

  return callstack;
}

const print_stack = function print_stack(err?: Error) {
  let cs: string[];
  try {
    cs = get_callstack(err);
  } catch (err2) {
    console.log("Could not fetch call stack.");
    return;
  }

  console.log("Callstack:");
  for (let i = 0; i < cs.length; i++) {
    console.log(cs[i]);
  }
};
const fetch_file = function fetch_file(path: string) {
  const url = location.origin + "/" + path;
  const req = new XMLHttpRequest();

  return new Promise(function (accept, reject) {
    req.open("GET", url);
    req.onreadystatechange = function (e) {
      if (req.status == 200 && req.readyState == 4) {
        accept(req.response);
      } else if (req.status >= 400) {
        reject(req.status);
      }
    };
    req.send();
  });
};

//from: https://en.wikipedia.org/wiki/Mersenne_Twister
function _int32(x: number): number {
  // Get the 31 least significant bits.
  return ~~(((1 << 30) - 1) & ~~x);
}

class MersenneRandom {
  index: number;
  mt: Uint32Array;

  constructor(seed: number = 0) {
    // Initialize the index to 0
    this.index = 624;
    this.mt = new Uint32Array(624);

    this.seed(seed);
  }

  random() {
    return this.extract_number() / (1 << 30);
  }

  seed(seed: number) {
    seed = ~~(seed * 8192);

    // Initialize the index to 0
    this.index = 624;
    this.mt.fill(0, 0, this.mt.length);

    this.mt[0] = seed; // Initialize the initial state to the seed

    for (let i = 1; i < 624; i++) {
      this.mt[i] = _int32(1812433253 * (this.mt[i - 1] ^ (this.mt[i - 1] >> 30)) + i);
    }
  }

  extract_number() {
    if (this.index >= 624) this.twist();

    let y = this.mt[this.index];

    // Right shift by 11 bits
    y = y ^ (y >> 11);
    // Shift y left by 7 and take the bitwise and of 2636928640
    y = y ^ ((y << 7) & 2636928640);
    // Shift y left by 15 and take the bitwise and of y and 4022730752
    y = y ^ ((y << 15) & 4022730752);
    // Right shift by 18 bits
    y = y ^ (y >> 18);

    this.index = this.index + 1;

    return _int32(y);
  }

  twist() {
    for (let i = 0; i < 624; i++) {
      // Get the most significant bit and add it to the less significant
      // bits of the next number
      const y = _int32((this.mt[i] & 0x80000000) + (this.mt[(i + 1) % 624] & 0x7fffffff));
      this.mt[i] = this.mt[(i + 397) % 624] ^ (y >> 1);

      if (y % 2 != 0) this.mt[i] = this.mt[i] ^ 0x9908b0df;
    }

    this.index = 0;
  }
}
typesystem.registerClass(MersenneRandom);

const _mt = new MersenneRandom(0);
const random = function () {
  return _mt.extract_number() / (1 << 30);
};
const seed = function (n: number) {
  //  console.trace("seed called");
  _mt.seed(n);
};
const _inthash_none = (1 << 31) - 1;
const hashsizes = [
  5, 11, 19, 37, 67, 127, 223, 383, 653, 1117, 1901, 3251, 5527, 9397, 15991, 27191, 46229, 78593,
  133631, 227177, 38619, 656587, 1116209, 1897561, 3225883, 5484019, 9322861, 15848867, 26943089,
  45803279, 77865577, 132371489, 225031553,
];

class IntHash extends Array<number> {
  used: number;
  cursize: number;
  totelem: number;
  forEach_rets: { next(): number[] };
  get_rets: { next(): number[] };

  constructor(totelem: number) {
    super();

    this.used = 0;
    this.cursize = 0;
    this.totelem = totelem + 1; //make sure we're including key
    this.forEach_rets = new cachering(function () {
      return new Array(totelem);
    }, 512);
    this.get_rets = new cachering(function () {
      return new Array(totelem);
    }, 512);

    this.nextsize();
  }

  toJSON() {
    const hash = [];
    const totelem = this.totelem;

    for (let i = 0; i < this.length; i += totelem) {
      if (this[i] == _inthash_none) {
        continue;
      }

      for (let j = 0; j < totelem; j++) {
        hash.push(this[i + j]);
      }
    }

    const ret = {
      size: this.used,
      totelem: this.totelem,
      hash: hash,
    };

    return ret;
  }

  static fromJSON(obj: { totelem: number; hash: number[] }) {
    const ret = new IntHash(obj.totelem - 1);
    const hash = obj.hash;
    const totelem = obj.totelem;
    const vals = new Array(totelem);

    //console.log("TOTELEM", totelem, hash);
    //return ret;

    for (let i = 0; i < hash.length; i += totelem) {
      for (let j = 0; j < totelem - 1; j++) {
        vals[j] = hash[i + j + 1];
      }

      ret.set(hash[i], vals);
    }

    return ret;
  }

  nextsize() {
    const size = hashsizes[++this.cursize];
    const old = this.length;
    const totelem = this.totelem;
    const cpy = [];
    for (let i = 0; i < this.length; i++) {
      cpy.push(this[i]);
    }
    //var cpy = this.slice(0, this.length);

    this.length = size * totelem;
    this.fill(_inthash_none, 0, this.length);
    this.used = 0;

    const vals = new Array(totelem - 1);

    for (let i = 0; i < cpy.length; i += totelem) {
      const key = cpy[i];
      if (key === _inthash_none) {
        continue;
      }

      for (let j = 1; j < totelem; j++) {
        vals[j - 1] = cpy[i + j];
      }

      this.set(key, vals);
    }
  }

  //args after key, vals is array of integer values
  // IntHash intentionally shadows the polyfilled Array.prototype.set with its
  // own (key, vals) hash-insert; the signatures are incompatible by design.
  // @ts-expect-error override clash with the global Array.set augmentation
  set(key: number, vals: number[]) {
    key = ~~key;

    const totelem = this.totelem;

    if (this.used > this.length / (3 * totelem)) {
      this.nextsize();
    }
    if (this.used > this.length / (3 * totelem)) {
      this.nextsize();
    }
    if (this.used > this.length / (3 * totelem)) {
      this.nextsize();
    }

    const size = ~~(this.length / totelem);
    let h = (key % size) * totelem;
    let _i = 0;
    let add = 1;
    while (this[h] != key && this[h] != _inthash_none) {
      add = add * 2 + 1;

      h = (key + add) % size;
      h = (h < 0 ? h + size : h) * totelem;

      if (_i++ > this.length * 50) {
        console.log("infinite loop in integer hash!", add, h, key, key % size, size, this.used);
        break;
      }
    }

    if (this[h] != key) this.used++;

    this[h] = key;

    for (let i = 1; i < totelem; i++) {
      this[h + i] = vals[i - 1];
    }
  }

  // @ts-expect-error override clash with Array.prototype.forEach (custom hash iter)
  forEach(cb: (key: number, vals: number[]) => void, thisvar?: object) {
    thisvar = thisvar == undefined ? this : thisvar;

    const totelem = this.totelem;
    const vals = this.forEach_rets.next();

    for (let i = 0; i < this.length; i += totelem) {
      const key = this[i];
      const vals = this.forEach_rets.next();

      if (key == _inthash_none && this[i + 1] == _inthash_none) {
        continue;
      }

      for (let j = 0; j < totelem - 1; j++) {
        vals[j] = this[i + 1 + j];
      }

      cb.call(thisvar, key, vals);
    }
  }

  get(key: number) {
    key = ~~key;

    const totelem = this.totelem;
    const size = ~~(this.length / totelem);
    let h = (key % size) * totelem;
    let _i = 0;
    let add = 1;
    while (this[h] != key && this[h] != _inthash_none) {
      add = add * 2 + 1;
      h = (key + add) % size;
      h = (h < 0 ? h + size : h) * totelem;

      if (_i++ > this.length * 10) {
        console.log("infinite loop in integer hash!", add, h, key, size, this.used);
        break;
      }
    }

    if (this[h] != key) return undefined;

    const ret = this.get_rets.next();
    for (let i = 0; i < totelem - 1; i++) {
      ret[i] = this[h + i + 1];
    }

    return ret;
  }

  has(key: number) {
    key = ~~key;

    const totelem = this.totelem;
    const size = ~~(this.length / totelem);
    let h = (key % size) * totelem;
    let _i = 0;
    let add = 1;
    while (this[h] != key && this[h] != _inthash_none) {
      add = add * 2 + 1;
      h = (key + add) % size;
      h = (h < 0 ? h + size : h) * totelem;

      if (_i++ > this.length * 10) {
        console.log("infinite loop in integer hash!", add, h, key, size, this.used);
        break;
      }
    }

    return this[h] == key;
  }

  //function remove(key) {
  //}
}
typesystem.registerClass(IntHash);

const test_inthash = function () {
  const ihash = new IntHash(1);
  const vals = [0, 1];

  seed(0);

  for (let i = 0; i < 29; i++) {
    const ri = ~~(random() * 4096);
    vals[0] = i;
    vals[1] = ri;

    console.log(ri, vals);
    ihash.set(ri, vals);
    ihash.set(ri, vals);
    ihash.set(ri, vals);
  }

  console.log("\n\n=====");
  let c = 0;
  ihash.forEach(function (key, vals) {
    //console.log(key, ""+vals);
    console.log(ihash.has(key), ihash.get(key), vals);
    c++;
  }, undefined);

  const json = JSON.stringify(ihash);
  console.log(json);
  console.log("\nused", ihash.used, c);

  const ihash2 = IntHash.fromJSON(JSON.parse(json));
  const json2 = JSON.stringify(ihash2);
  const ihash3 = IntHash.fromJSON(JSON.parse(json2));
  const json3 = JSON.stringify(ihash3);
  console.log(json2, "\n", json3, "\n", json2 == json3);

  return undefined;
};

export {
  time_ms,
  RandomArray,
  cachering,
  SetIter,
  set,
  hashtable,
  IDGen,
  print_stack,
  fetch_file,
  MersenneRandom,
  IntHash,
  random,
  seed,
  test_inthash,
};
